import { proxy } from 'valtio'
import { devtools } from 'valtio/utils'

import type { EmptyObject } from '@/utils/primitives'

import { CellKey } from '../cell/cell.schema'

import type { AlertDialogProps } from './AlertDialogModal'

/**
 * Centralized registry for all Rundown modals.
 */
type ModalRegistry = {
  ALERT_DIALOG: {
    props: AlertDialogProps
    result: boolean
  }

  EDITOR_CONFLICT_DIALOG: {
    props: { cellKey: CellKey }
    result: boolean
  }

  SHORTCUTS: {
    props: EmptyObject
    result: void
    toggleable: true
  }

  MENTIONS: {
    props: EmptyObject
    result: void
    toggleable: true
  }

  VARIABLES: {
    props: EmptyObject
    result: void
    toggleable: true
  }

  CELL_HISTORY: {
    props: { cellKey: CellKey }
    result: void
  }
}

// #region ============== STORE

type ModalConfig = {
  id: string
  payload: ModalPayload
  /** Resolve function. Uses `unknown` internally, but strictly typed at boundary */
  resolve: (value: unknown) => void
  options?: {}
}

interface ModalState {
  stack: ModalConfig[]
}

/**
 * Modal Store
 */
export const modalStore = proxy<ModalState>({
  stack: [],
})

// #endregion

/**
 * Modal Store actions
 */
export const modalActions = {
  /**
   * Open modal
   */
  open<K extends ModalKey>(
    key: K,
    props: ModalProps<K>,
    options?: ModalConfig['options'],
  ): Promise<ModalResult<K>> {
    return new Promise<ModalResult<K>>((resolve) => {
      const id = Math.random().toString(36).substring(2, 9)

      modalStore.stack.push({
        id,
        // Necessary assertions, type-checked at boundary
        payload: { key, props } as ModalPayload,
        resolve: resolve as (value: unknown) => void,
        options,
      })
    })
  },

  /**
   * Toggle modal, ie. open if not already open, otherwise close.
   *
   * This method only works on modals marked `toggleable: true`.
   */
  toggle<K extends ToggleableModalKey>(
    key: K,
    props: ModalProps<K>,
    options?: ModalConfig['options'],
  ): void {
    const isOpen = modalStore.stack.find((m) => m.payload.key === key)

    if (isOpen) {
      resolveModal(isOpen.id, undefined)
    } else {
      void modalActions.open(key, props, options)
    }
  },
}

/**
 * Resolve (close) modal with result
 *
 * @internal Only for use in modalStore and ModalManager.
 */
export function resolveModal(id: string, result: unknown): void {
  const modal = modalStore.stack.find((m) => m.id === id)
  if (!modal) return

  // Remove first to prevent repeated calls, then resolve
  modalStore.stack = modalStore.stack.filter((m) => m.id !== id)

  modal.resolve(result)
}

// #region ============== TYPE HELPERS

type ModalKey = keyof ModalRegistry

type ModalProps<K extends ModalKey> = ModalRegistry[K]['props']

type ModalResult<K extends ModalKey> = ModalRegistry[K]['result']

type ModalDefinition = {
  [K in ModalKey]: {
    key: K
  } & ModalRegistry[K]
}[ModalKey]

type ModalPayload<K extends ModalKey = ModalKey> = {
  [P in K]: {
    key: P
    props: ModalProps<P>
  }
}[K]

type EnsureToggleableReturnsVoid<T extends { result: void }> = T

type ToggleableModal = EnsureToggleableReturnsVoid<
  Extract<ModalDefinition, { toggleable: true }>
>

type ToggleableModalKey = ToggleableModal['key']

export type ModalResolverProps = {
  payload: ModalPayload
  onResolve: (data: unknown) => void
}

export type ModalComponentProps<K extends ModalKey> = {
  props: ModalProps<K>
  onResolve: (data: ModalResult<K>) => void
}

// #endregion

// #region ============== DEV

if (import.meta.env.DEV) {
  devtools(modalStore, { name: 'modalStore', enabled: true })
}

// #endregion
